import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        success:
          "border-status-success-border bg-status-success-bg text-status-success-fg [a&]:hover:opacity-90",
        warning:
          "border-status-warning-border bg-status-warning-bg text-status-warning-fg [a&]:hover:opacity-90",
        danger:
          "border-status-danger-border bg-status-danger-bg text-status-danger-fg [a&]:hover:opacity-90",
        info:
          "border-status-info-border bg-status-info-bg text-status-info-fg [a&]:hover:opacity-90",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 [a&]:hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300",
        pink:
          "border-pink-200 bg-pink-50 text-pink-700 [a&]:hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 [a&]:hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300",
        beta:
          "border-indigo-200 bg-indigo-50 text-indigo-700 [a&]:hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950 dark:text-indigo-300",
      },
      size: {
        default: "",
        // Count chip in a filter trigger.
        count: "rounded-sm px-1 font-normal",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
